import React, { useState } from 'react';
import {
  FiArrowLeft, FiGrid, FiFileText, FiUsers, FiBriefcase, FiShield, FiAlertTriangle,
  FiPlus, FiX, FiEdit2, FiTrash2,
} from 'react-icons/fi';
import styles from '../FichaCadastral.module.css';

let idAuto = 1;
const novoId = () => `id-${idAuto++}`;

function quadro(titulo, labels) {
  return { id: novoId(), titulo, campos: labels.map(label => ({ id: novoId(), label, valor: '' })) };
}

export const FICHA_VAZIA = {
  regime:          '',
  folha:           '',
  rotinasMapeadas: '',
  responsaveis: { fiscal: '', folhaDp: '', contabil: '' },
  contato:      { preferencial: '', documentos: '', certificado: '' },

  fiscal: { quadros: [
    quadro('Enquadramento', ['Regime tributário', 'Anexo (Simples)', 'Atividade principal', 'CNAE']),
    quadro('Inscrições e obrigações', ['Inscrição estadual', 'Inscrição municipal', 'Substituição tributária']),
  ] },
  folhaDp: { quadros: [
    quadro('Quadro de pessoal', ['Funcionários', 'Sócios com pró-labore', 'Convenção coletiva']),
    quadro('Rotina', ['Fechamento da folha', 'eSocial']),
  ] },
  contabil: { quadros: [
    quadro('Escrituração', ['Tipo de escrituração', 'Regime de apuração', 'Encerramento de balanço']),
  ] },
  societario: { quadros: [
    quadro('Estrutura societária', ['Tipo societário', 'Sócios', 'Capital social', 'Data de abertura']),
  ] },
  atencao: { pontos: [] },
};

const ABAS = [
  { id: 'visao-geral', label: 'Visão geral', icon: FiGrid },
  { id: 'fiscal',      label: 'Fiscal',      icon: FiFileText },
  { id: 'folha-dp',    label: 'Folha / DP',  icon: FiUsers },
  { id: 'contabil',    label: 'Contábil',    icon: FiBriefcase },
  { id: 'societario',  label: 'Societário',  icon: FiShield },
  { id: 'atencao',     label: 'Atenção',     icon: FiAlertTriangle },
];

/* ── helpers de campo (aba Visão geral, que tem layout fixo) ── */

function CampoStat({ label, valor, onChange }) {
  return (
    <div className={styles.statCard}>
      <span className={styles.statLabel}>{label}</span>
      <input
        className={styles.statInput}
        value={valor}
        placeholder="—"
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

function CampoLinha({ label, valor, onChange }) {
  return (
    <div className={styles.linhaCampo}>
      <span className={styles.linhaLabel}>{label}</span>
      <input
        className={styles.linhaInput}
        value={valor}
        placeholder="—"
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

/* ── Visão geral ── */
function VisaoGeralTab({ dados, onChange }) {
  const d = { ...FICHA_VAZIA, ...dados,
    responsaveis: { ...FICHA_VAZIA.responsaveis, ...dados?.responsaveis },
    contato:      { ...FICHA_VAZIA.contato,      ...dados?.contato },
  };
  const pontosAtivos = (dados?.atencao?.pontos ?? []).filter(p => p.ativo).length;

  function set(campo, valor) { onChange({ ...d, [campo]: valor }); }
  function setResp(campo, valor) { onChange({ ...d, responsaveis: { ...d.responsaveis, [campo]: valor } }); }
  function setContato(campo, valor) { onChange({ ...d, contato: { ...d.contato, [campo]: valor } }); }

  return (
    <div className={styles.visaoGeral}>
      <div className={styles.statsGrid}>
        <CampoStat label="Regime"           valor={d.regime}          onChange={v => set('regime', v)} />
        <CampoStat label="Folha"            valor={d.folha}           onChange={v => set('folha', v)} />
        <CampoStat label="Rotinas mapeadas" valor={d.rotinasMapeadas} onChange={v => set('rotinasMapeadas', v)} />
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Pontos de atenção</span>
          <span className={`${styles.statValor} ${pontosAtivos > 0 ? styles.statValorAlerta : ''}`}>
            {pontosAtivos > 0 ? `${pontosAtivos} ativo${pontosAtivos !== 1 ? 's' : ''}` : 'Nenhum'}
          </span>
        </div>
      </div>

      <div className={styles.duasColunas}>
        <div className={styles.secaoCard}>
          <p className={styles.secaoTitulo}>Responsáveis internos</p>
          <CampoLinha label="Fiscal"     valor={d.responsaveis.fiscal}   onChange={v => setResp('fiscal', v)} />
          <CampoLinha label="Folha / DP" valor={d.responsaveis.folhaDp}  onChange={v => setResp('folhaDp', v)} />
          <CampoLinha label="Contábil"   valor={d.responsaveis.contabil} onChange={v => setResp('contabil', v)} />
        </div>

        <div className={styles.secaoCard}>
          <p className={styles.secaoTitulo}>Contato do cliente</p>
          <CampoLinha label="Preferencial" valor={d.contato.preferencial} onChange={v => setContato('preferencial', v)} />
          <CampoLinha label="Documentos"   valor={d.contato.documentos}   onChange={v => setContato('documentos', v)} />
          <CampoLinha label="Certificado"  valor={d.contato.certificado}  onChange={v => setContato('certificado', v)} />
        </div>
      </div>
    </div>
  );
}

/* ── Aba genérica de quadros dinâmicos (Fiscal, Folha/DP, Contábil, Societário) ──
   O usuário monta a própria estrutura: cria quadros e, dentro de cada um,
   adiciona/renomeia/remove livremente as informações (label + valor). ── */
function QuadrosTab({ dados, onChange, chave }) {
  const quadros = dados?.[chave]?.quadros ?? [];

  function commit(novosQuadros) {
    onChange({ ...dados, [chave]: { quadros: novosQuadros } });
  }

  function adicionarQuadro() {
    const titulo = window.prompt('Nome do quadro (ex: Enquadramento)');
    if (!titulo?.trim()) return;
    commit([...quadros, { id: novoId(), titulo: titulo.trim(), campos: [] }]);
  }

  function renomearQuadro(q) {
    const titulo = window.prompt('Nome do quadro', q.titulo);
    if (!titulo?.trim()) return;
    commit(quadros.map(x => x.id === q.id ? { ...x, titulo: titulo.trim() } : x));
  }

  function removerQuadro(q) {
    if (!window.confirm(`Remover o quadro "${q.titulo}" e todas as informações dentro dele?`)) return;
    commit(quadros.filter(x => x.id !== q.id));
  }

  function adicionarCampo(qId) {
    commit(quadros.map(q => q.id === qId
      ? { ...q, campos: [...q.campos, { id: novoId(), label: '', valor: '' }] }
      : q));
  }

  function atualizarCampo(qId, campoId, mudanca) {
    commit(quadros.map(q => q.id !== qId ? q : {
      ...q,
      campos: q.campos.map(c => c.id === campoId ? { ...c, ...mudanca } : c),
    }));
  }

  function removerCampo(qId, campoId) {
    commit(quadros.map(q => q.id !== qId ? q : { ...q, campos: q.campos.filter(c => c.id !== campoId) }));
  }

  return (
    <div>
      {quadros.length === 0 && (
        <p className={styles.placeholderSub} style={{ marginBottom: 14 }}>
          Nenhum quadro criado ainda. Adicione um quadro e, dentro dele, as informações que quiser.
        </p>
      )}

      <div className={styles.duasColunas}>
        {quadros.map(q => (
          <div key={q.id} className={styles.secaoCard}>
            <div className={styles.quadroHeader}>
              <p className={styles.secaoTitulo}>{q.titulo}</p>
              <div className={styles.quadroAcoes}>
                <button type="button" className={styles.btnIconeSutil} title="Renomear quadro"
                  onClick={() => renomearQuadro(q)}>
                  <FiEdit2 size={12} />
                </button>
                <button type="button" className={styles.btnIconeSutil} title="Remover quadro"
                  onClick={() => removerQuadro(q)}>
                  <FiTrash2 size={12} />
                </button>
              </div>
            </div>

            {q.campos.map(c => (
              <div key={c.id} className={styles.linhaCampoEditavel}>
                <input
                  className={styles.linhaLabelInput}
                  value={c.label}
                  placeholder="Nome da informação"
                  onChange={e => atualizarCampo(q.id, c.id, { label: e.target.value })}
                />
                <input
                  className={styles.linhaInput}
                  value={c.valor}
                  placeholder="—"
                  onChange={e => atualizarCampo(q.id, c.id, { valor: e.target.value })}
                />
                <button type="button" className={styles.atencaoRemover} title="Remover informação"
                  onClick={() => removerCampo(q.id, c.id)}>
                  <FiX size={12} />
                </button>
              </div>
            ))}

            <button type="button" className={styles.btnAdicionarCampo} onClick={() => adicionarCampo(q.id)}>
              <FiPlus size={12} /> Adicionar informação
            </button>
          </div>
        ))}
      </div>

      <button type="button" className={styles.btnAdicionarQuadro} onClick={adicionarQuadro}>
        <FiPlus size={13} /> Adicionar quadro
      </button>
    </div>
  );
}

/* ── Atenção — lista de pontos ativos/resolvidos ── */
function AtencaoTab({ dados, onChange }) {
  const pontos = dados?.atencao?.pontos ?? [];
  const [novoTexto, setNovoTexto] = useState('');

  function commit(novosPontos) {
    onChange({ ...dados, atencao: { pontos: novosPontos } });
  }

  function adicionar() {
    if (!novoTexto.trim()) return;
    commit([...pontos, { id: novoId(), texto: novoTexto.trim(), ativo: true }]);
    setNovoTexto('');
  }

  function alternar(id) {
    commit(pontos.map(p => p.id === id ? { ...p, ativo: !p.ativo } : p));
  }

  function remover(id) {
    commit(pontos.filter(p => p.id !== id));
  }

  return (
    <div className={styles.secaoCard}>
      <p className={styles.secaoTitulo}>Pontos de atenção</p>

      <div className={styles.atencaoNovoRow}>
        <input
          className={styles.atencaoNovoInput}
          value={novoTexto}
          placeholder="Ex: Certificado digital vence em 30 dias"
          onChange={e => setNovoTexto(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && adicionar()}
        />
        <button type="button" className={styles.btnAdicionarPonto} onClick={adicionar}>
          <FiPlus size={13} /> Adicionar
        </button>
      </div>

      {pontos.length === 0 ? (
        <p className={styles.placeholderSub}>Nenhum ponto de atenção registrado.</p>
      ) : (
        <ul className={styles.atencaoLista}>
          {pontos.map(p => (
            <li key={p.id} className={styles.atencaoItem}>
              <button
                type="button"
                className={`${styles.atencaoStatus} ${p.ativo ? styles.atencaoStatusAtivo : ''}`}
                onClick={() => alternar(p.id)}
                title={p.ativo ? 'Marcar como resolvido' : 'Reativar'}
              />
              <span className={p.ativo ? styles.atencaoTexto : styles.atencaoTextoResolvido}>
                {p.texto}
              </span>
              <button type="button" className={styles.atencaoRemover} onClick={() => remover(p.id)}>
                <FiX size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const TAB_COMPONENTES = {
  'visao-geral': VisaoGeralTab,
  'fiscal':      props => <QuadrosTab {...props} chave="fiscal" />,
  'folha-dp':    props => <QuadrosTab {...props} chave="folhaDp" />,
  'contabil':    props => <QuadrosTab {...props} chave="contabil" />,
  'societario':  props => <QuadrosTab {...props} chave="societario" />,
  'atencao':     AtencaoTab,
};

/* ── Componente compartilhado: usado tanto na Ficha Cadastral de um cliente
   quanto na aba "Modelos de Ficha" de Configurações (edição do modelo base) ── */
export default function FichaCadastralTabs({ titulo, subtitulo, dados, onChange, onVoltar, acoesExtra }) {
  const [abaAtiva, setAbaAtiva] = useState('visao-geral');
  const AbaAtual = TAB_COMPONENTES[abaAtiva];

  return (
    <div className={styles.detalhe}>
      <div className={styles.detalheHeader}>
        {onVoltar && (
          <button className={styles.btnVoltar} onClick={onVoltar}><FiArrowLeft size={13} /> Voltar</button>
        )}
        <div className={styles.detalheTitulos}>
          <span className={styles.detalheTitulo}>{titulo}</span>
          {subtitulo && <span className={styles.detalheSubtitulo}>{subtitulo}</span>}
        </div>
        {acoesExtra}
      </div>

      <div className={styles.tabs}>
        {ABAS.map(aba => {
          const Icone = aba.icon;
          return (
            <button key={aba.id}
              className={`${styles.tabBtn} ${abaAtiva === aba.id ? styles.tabBtnAtiva : ''}`}
              onClick={() => setAbaAtiva(aba.id)}>
              <Icone size={14} /> {aba.label}
            </button>
          );
        })}
      </div>

      <div className={styles.conteudo}>
        <AbaAtual dados={dados} onChange={onChange} />
      </div>
    </div>
  );
}
